import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft, Download, Share2, FileText, FileSpreadsheet } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#7c3aed', '#2563eb', '#06b6d4', '#d946ef', '#f43f5e'];

const DashboardViewPage = () => {
  const { id } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadDashboard();
  }, [id]);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setAiAnalysis(JSON.parse(response.data.ai_analysis));
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dashboard.file_name}_analysis.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Downloaded successfully!');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDownloadFormat = async (format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      
      const response = await axios.get(`${API}/dashboard/${id}/download/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const extensions = {
        'excel': 'xlsx',
        'pdf': 'pdf',
        'word': 'docx',
        'ppt': 'pptx'
      };
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dashboard.file_name}_dashboard.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Download failed';
      toast.error(`Failed to download ${format.toUpperCase()}: ${errorMessage}`);
    }
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/share/${dashboard.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard!');
  };

  const renderChart = (chart, index) => {
    const commonProps = {
      data: chart.data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chart.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Bar dataKey={chart.yKey} fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Line type="monotone" dataKey={chart.yKey} stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Area type="monotone" dataKey={chart.yKey} stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chart.data}
                dataKey={chart.dataKey}
                nameKey={chart.nameKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {chart.data.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl text-muted-foreground">Dashboard not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 h-20 flex items-center justify-between px-8 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold">{dashboard.file_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleShare} variant="outline" size="sm" data-testid="share-dashboard-btn" className="rounded-full">
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
          <Button onClick={handleDownload} variant="outline" size="sm" data-testid="download-dashboard-btn" className="rounded-full">
            <Download className="h-4 w-4 mr-2" /> Download Excel
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-8 py-12 max-w-7xl">
        {/* AI Analysis */}
        {aiAnalysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Executive Summary */}
            <div className="glass rounded-2xl p-8" data-testid="executive-summary">
              <h2 className="text-2xl font-bold mb-4 text-primary">Executive Summary</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{aiAnalysis.executive_summary}</p>
            </div>

            {/* Charts */}
            {dashboard.chart_data && dashboard.chart_data.length > 0 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold">Visualizations</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {dashboard.chart_data.map((chart, index) => (
                    <div key={index} className="glass rounded-2xl p-6" data-testid={`chart-${index}`}>
                      <h3 className="text-lg font-semibold mb-4">{chart.title}</h3>
                      {renderChart(chart, index)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Insights */}
            {aiAnalysis.detailed_insights && aiAnalysis.detailed_insights.length > 0 && (
              <div className="glass rounded-2xl p-8" data-testid="key-insights">
                <h2 className="text-2xl font-bold mb-4 text-accent">Detailed Insights</h2>
                <div className="space-y-4">
                  {aiAnalysis.detailed_insights.map((insight, index) => (
                    <div key={index} className="border-l-4 border-accent pl-4">
                      <div className="text-xs text-chart-3 uppercase tracking-wider mb-1">{insight.category}</div>
                      <h3 className="text-lg font-semibold mb-2">{insight.title}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-2">{insight.description}</p>
                      <p className="text-primary italic text-sm"><strong>Impact:</strong> {insight.impact}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* KPI Cards */}
            {aiAnalysis.kpi_cards && aiAnalysis.kpi_cards.length > 0 && (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-6 text-primary">Key Performance Indicators</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {aiAnalysis.kpi_cards.map((kpi, index) => (
                    <div key={index} className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-xl p-6 text-center">
                      <h4 className="text-sm text-muted-foreground uppercase tracking-wider mb-2">{kpi.title}</h4>
                      <div className="text-4xl font-bold text-primary mb-2">{kpi.value}</div>
                      <p className="text-sm text-muted-foreground">{kpi.insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Statistical Analysis */}
            {aiAnalysis.statistical_analysis && aiAnalysis.statistical_analysis.length > 0 && (
              <div className="glass rounded-2xl p-8" data-testid="statistical-findings">
                <h2 className="text-2xl font-bold mb-4 text-chart-3">Statistical Analysis</h2>
                <div className="space-y-3">
                  {aiAnalysis.statistical_analysis.map((stat, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-chart-3 mt-1">•</span>
                      <div>
                        <strong className="text-foreground">{stat.metric}:</strong>{' '}
                        <span className="text-muted-foreground leading-relaxed">{stat.finding}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
              <div className="glass rounded-2xl p-8" data-testid="recommendations">
                <h2 className="text-2xl font-bold mb-4 text-chart-4">Actionable Recommendations</h2>
                <div className="space-y-4">
                  {aiAnalysis.recommendations.map((rec, index) => {
                    const priorityColors = {
                      'High': 'border-red-500 bg-red-500/10',
                      'Medium': 'border-yellow-500 bg-yellow-500/10',
                      'Low': 'border-green-500 bg-green-500/10'
                    };
                    const priorityColor = priorityColors[rec.priority] || priorityColors['Medium'];
                    
                    return (
                      <div key={index} className={`border-l-4 ${priorityColor} pl-4 py-3 rounded-r-lg`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-chart-4/20 text-chart-4">
                            {rec.priority} Priority
                          </span>
                          <span className="text-xs font-medium text-accent uppercase">{rec.category}</span>
                        </div>
                        <p className="text-foreground font-medium mb-2">{rec.recommendation}</p>
                        <p className="text-sm text-muted-foreground italic">
                          <strong>Expected Impact:</strong> {rec.expected_impact}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Download Options */}
            <div className="glass rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Download Dashboard</h2>
              <p className="text-muted-foreground mb-6">Export your dashboard analysis in your preferred format</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button
                  onClick={() => handleDownloadFormat('excel')}
                  className="flex flex-col items-center gap-2 h-auto py-6"
                  data-testid="download-excel-btn"
                >
                  <FileSpreadsheet className="h-8 w-8" />
                  <span>Excel (.xlsx)</span>
                  <span className="text-xs opacity-70">Data + Analysis</span>
                </Button>
                
                <Button
                  onClick={() => handleDownloadFormat('pdf')}
                  className="flex flex-col items-center gap-2 h-auto py-6"
                  data-testid="download-pdf-btn"
                >
                  <FileText className="h-8 w-8" />
                  <span>PDF Document</span>
                  <span className="text-xs opacity-70">Professional Report</span>
                </Button>
                
                <Button
                  onClick={() => handleDownloadFormat('word')}
                  className="flex flex-col items-center gap-2 h-auto py-6"
                  data-testid="download-word-btn"
                >
                  <FileText className="h-8 w-8" />
                  <span>Word (.docx)</span>
                  <span className="text-xs opacity-70">Editable Document</span>
                </Button>
                
                <Button
                  onClick={() => handleDownloadFormat('ppt')}
                  className="flex flex-col items-center gap-2 h-auto py-6"
                  data-testid="download-ppt-btn"
                >
                  <FileText className="h-8 w-8" />
                  <span>PowerPoint (.pptx)</span>
                  <span className="text-xs opacity-70">Presentation</span>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DashboardViewPage;